import { API_BASE } from '@/shared/services/api';
import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/shared/contexts/AuthContext';
import { toast } from 'sonner';
import { cn } from '@/shared/utils';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/shared/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { ScrollArea, ScrollBar } from '@/shared/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Switch } from '@/shared/components/ui/switch';
import {
  Shield, ShieldCheck, ShieldX, ShieldQuestion, Users, UserPlus, UserMinus,
  Plus, Trash2, Edit, RefreshCw, Search, Filter, Eye, EyeOff,
  Hammer, UserCog, Car, Warehouse, Check, X, Minus, ChevronDown, ChevronRight,
  LayoutDashboard, Package, Truck, Settings, ScanLine, Box, MapPin, Thermometer,
  Receipt, BookOpen, Target, MessageSquare, FileText, Activity, AlertTriangle,
  Gauge, Bell, History, Upload, Download, PenTool, Camera, KeyRound, RotateCcw,
  Play, CheckCircle, PackageCheck, PackageX, ArrowDownToLine, MoveDown, ShoppingCart,
  PackageSearch, BoxSelect, Send, Route, UserPlus as UserPlusIcon, Building2, BarChart3,
  FileCheck, ClipboardList, Pencil, Cog, FilePlus, Wallet, Building, Crosshair, Mail,
  MessageCircle, Clock, Calendar, CalendarDays, CalendarPlus, DollarSign, Lock, Inbox,
  CalendarCheck, CalendarClock, Store, LayoutGrid, Kanban
} from 'lucide-react';

const API_URL = API_BASE;

// Icon mapping for capabilities
const ICON_MAP = {
  LayoutDashboard, Package, Truck, Settings, ScanLine, Box, MapPin, Thermometer,
  Receipt, BookOpen, Target, MessageSquare, FileText, Activity, AlertTriangle,
  Gauge, Bell, History, Upload, Download, PenTool, Camera, KeyRound, RotateCcw,
  Play, CheckCircle, PackageCheck, PackageX, ArrowDownToLine, MoveDown, ShoppingCart,
  PackageSearch, BoxSelect, Send, Route, UserPlus: UserPlusIcon, Building2, BarChart3,
  Shield, ShieldCheck, Users, UserCog, Car, Warehouse, Eye, FileCheck, ClipboardList,
  Pencil, Cog, FilePlus, Wallet, Building, Crosshair, Mail, MessageCircle,
  Clock, Calendar, CalendarDays, CalendarPlus, DollarSign, Lock, Inbox, CalendarCheck,
  CalendarClock, Store, LayoutGrid, Kanban
};

// Module display names and colors
const MODULE_CONFIG = {
  wms: { name: 'WMS', color: '#2563eb', icon: Warehouse },
  ezparcel: { name: 'EzParcel', color: '#dc2626', icon: Truck },
  driver: { name: 'Driver Mobile', color: '#f59e0b', icon: Car },
  mobile: { name: 'Mobile WMS', color: '#06b6d4', icon: ScanLine },
  trails: { name: 'Trails', color: '#8b5cf6', icon: Activity },
  coldchain: { name: 'Cold Chain', color: '#14b8a6', icon: Thermometer },
  geofencing: { name: 'Geofencing', color: '#22c55e', icon: MapPin },
  admin: { name: 'Administration', color: '#ef4444', icon: Shield },
  finance: { name: 'Finance', color: '#16a34a', icon: Receipt },
  procurement: { name: 'Procurement', color: '#4f46e5', icon: ShoppingCart },
  crm: { name: 'CRM', color: '#ec4899', icon: Users },
  communications: { name: 'Communications', color: '#8b5cf6', icon: MessageSquare },
  oig: { name: 'OIG', color: '#06b6d4', icon: FileText },
  hr: { name: 'Human Resources', color: '#7c3aed', icon: Users },
  accounting: { name: 'Accounting', color: '#059669', icon: BookOpen },
  eztask: { name: 'EzTask', color: '#f59e0b', icon: ClipboardList },
  calendar: { name: 'Calendar', color: '#3b82f6', icon: LayoutDashboard },
  ecommerce: { name: 'Ecommerce', color: '#10b981', icon: ShoppingCart },
  cns: { name: 'Social Media', color: '#ec4899', icon: MessageCircle },
  reports: { name: 'Report Builder', color: '#3b82f6', icon: BarChart3 }
};

// Template icon mapping
const TEMPLATE_ICON_MAP = {
  Eye, Hammer, UserCog, ShieldCheck, ShieldX, Settings, Shield,
  Warehouse, Truck, DollarSign: Receipt
};

// Default templates (fallback)
const DEFAULT_TEMPLATES = [
  { value: 'viewer', label: 'Viewer', desc: 'Read-only access', icon: Eye, color: '#64748b' },
  { value: 'operator', label: 'Operator', desc: 'Execute operations', icon: Hammer, color: '#22c55e' },
  { value: 'manager', label: 'Manager', desc: 'Full module access', icon: UserCog, color: '#3b82f6' },
  { value: 'administrator', label: 'Administrator', desc: 'Full system access', icon: ShieldCheck, color: '#8b5cf6' },
  { value: 'denied', label: 'Denied', desc: 'No access', icon: ShieldX, color: '#ef4444' },
  { value: 'custom', label: 'Custom', desc: 'Custom permissions', icon: Settings, color: '#6366f1' }
];

// Permission state component - 3-state toggle
const PermissionCell = ({ state, onToggle, isReadOnly, capabilityName, isSensitive, isUngrantable }) => {
  const getNextState = (current) => {
    if (current === 'allow') return 'deny';
    if (current === 'deny') return 'not_set';
    return 'allow';
  };

  const stateConfig = {
    allow: { icon: Check, color: 'bg-emerald-500 text-white', label: 'Allowed' },
    deny: { icon: X, color: 'bg-red-500 text-white', label: 'Denied' },
    not_set: { icon: Minus, color: 'bg-slate-300 dark:bg-slate-600 text-slate-600 dark:text-slate-300', label: 'Not Set' }
  };

  const config = stateConfig[state] || stateConfig.not_set;
  const Icon = config.icon;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={() => !isReadOnly && onToggle(getNextState(state))}
            disabled={isReadOnly}
            className={cn(
              'w-8 h-8 rounded flex items-center justify-center transition-all duration-150',
              config.color,
              isReadOnly ? 'opacity-50 cursor-not-allowed' : 'hover:scale-110 cursor-pointer',
              isSensitive && !isUngrantable && 'ring-2 ring-amber-400 ring-offset-1',
              isUngrantable && 'ring-2 ring-slate-400 ring-offset-1 ring-dashed'
            )}
            data-testid={`permission-cell-${capabilityName}`}
          >
            <Icon className="h-4 w-4" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">
          <p className="font-medium">{capabilityName}</p>
          <p className="text-xs text-muted-foreground">{config.label}</p>
          {isSensitive && <p className="text-xs text-amber-500">⚠️ Sensitive Permission</p>}
          {isUngrantable && <p className="text-xs text-slate-400">🔒 Cannot grant (outside your scope)</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Profile row in the matrix
const ProfileRow = ({ profile, columns, onPermissionChange, onEdit, onDelete, expandedModules, isCapabilityGrantable }) => {
  const Icon = ICON_MAP[profile.icon] || Shield;
  
  return (
    <div className="flex items-center border-b border-border hover:bg-muted/30 transition-colors">
      {/* Profile info - sticky left column */}
      <div className="sticky left-0 z-10 bg-card flex items-center gap-3 p-3 min-w-[220px] w-[220px] border-r border-border">
        <div 
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: profile.color }}
        >
          <Icon className="h-4 w-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{profile.name}</p>
          <div className="flex items-center gap-1.5">
            <Badge variant="outline" className="text-[10px] px-1.5 py-0">
              {profile.template}
            </Badge>
            {profile.is_system && (
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0">System</Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEdit(profile)}>
            <Edit className="h-3.5 w-3.5" />
          </Button>
          {!profile.is_system && (
            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => onDelete(profile)}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      
      {/* Permission cells */}
      <div className="flex">
        {columns.map((cap, idx) => {
          const permission = profile.permissions?.find(p => p.capability_id === cap.id);
          const state = permission?.state || 'not_set';
          const isModuleStart = cap.is_module_start;
          const canGrant = isCapabilityGrantable ? isCapabilityGrantable(cap.code) : true;
          
          return (
            <div 
              key={cap.id} 
              className={cn(
                'p-1.5 flex items-center justify-center',
                isModuleStart && 'border-l-2 border-l-slate-400/30',
                !canGrant && 'opacity-40 bg-muted/30'
              )}
              style={{ minWidth: '44px' }}
            >
              <PermissionCell
                state={state}
                onToggle={(newState) => onPermissionChange(profile.id, cap.id, newState)}
                isReadOnly={(profile.is_system && profile.template === 'administrator') || !canGrant}
                capabilityName={cap.name}
                isSensitive={cap.is_sensitive}
                isUngrantable={!canGrant}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Column header for a capability
const CapabilityHeader = ({ capability, moduleColor }) => {
  const Icon = ICON_MAP[capability.icon] || Settings;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div 
            className={cn(
              'flex flex-col items-center justify-end p-2 cursor-help',
              capability.is_module_start && 'border-l-2'
            )}
            style={{ 
              minWidth: '44px',
              borderColor: capability.is_module_start ? moduleColor : 'transparent'
            }}
          >
            <div 
              className="w-7 h-7 rounded flex items-center justify-center mb-1"
              style={{ backgroundColor: `${moduleColor}20` }}
            >
              <Icon className="h-4 w-4" style={{ color: moduleColor }} />
            </div>
            <span className="text-[10px] text-muted-foreground text-center leading-tight max-w-[40px] truncate">
              {capability.name.split(' ').slice(-1)[0]}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="font-medium">{capability.name}</p>
          <p className="text-xs text-muted-foreground">{capability.code}</p>
          {capability.is_sensitive && <p className="text-xs text-amber-500">⚠️ Sensitive</p>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Module group header
const ModuleGroupHeader = ({ module, capabilities, isExpanded, onToggle }) => {
  const config = MODULE_CONFIG[module] || { name: module, color: '#6366f1', icon: Box };
  const Icon = config.icon;
  
  return (
    <button
      onClick={onToggle}
      className="flex items-center gap-2 px-3 py-2 hover:bg-muted/50 rounded-lg transition-colors w-full"
    >
      <div 
        className="w-6 h-6 rounded flex items-center justify-center"
        style={{ backgroundColor: config.color }}
      >
        <Icon className="h-3.5 w-3.5 text-white" />
      </div>
      <span className="font-medium text-sm">{config.name}</span>
      <Badge variant="outline" className="ml-auto text-xs">{capabilities.length}</Badge>
      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
    </button>
  );
};

// Create/Edit Profile Dialog
const ProfileDialog = ({ profile, isOpen, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    template: 'custom',
    color: '#6366f1',
    icon: 'shield'
  });
  
  const [templates, setTemplates] = useState(DEFAULT_TEMPLATES);
  
  // Fetch templates from API
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const API_URL = API_BASE;
        const response = await fetch(`${API_URL}/api/reference/permission-templates`);
        if (response.ok) {
          const data = await response.json();
          const mappedTemplates = data.map(t => ({
            value: t.code.toLowerCase(),
            label: t.label,
            desc: t.description || t.label,
            icon: TEMPLATE_ICON_MAP[t.icon] || Shield,
            color: t.color || '#6366f1'
          }));
          if (mappedTemplates.length > 0) {
            setTemplates(mappedTemplates);
          }
        }
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      }
    };
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (profile) {
      setForm({
        name: profile.name || '',
        description: profile.description || '',
        template: profile.template || 'custom',
        color: profile.color || '#6366f1',
        icon: profile.icon || 'shield'
      });
    } else {
      setForm({ name: '', description: '', template: 'custom', color: '#6366f1', icon: 'shield' });
    }
  }, [profile, isOpen]);

  const handleSubmit = () => {
    if (!form.name.trim()) {
      toast.error('Profile name is required');
      return;
    }
    onSave(form);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{profile ? 'Edit Profile' : 'Create New Profile'}</DialogTitle>
          <DialogDescription>
            {profile ? 'Modify the profile settings below.' : 'Create a new access profile with custom permissions.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name">Profile Name</Label>
            <Input
              id="name"
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g., Warehouse Supervisor"
              data-testid="profile-name-input"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Describe the role and responsibilities..."
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label>Template</Label>
            <Select value={form.template} onValueChange={(v) => setForm(f => ({ ...f, template: v }))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {templates.map(t => {
                  const TIcon = t.icon;
                  return (
                    <SelectItem key={t.value} value={t.value}>
                      <div className="flex items-center gap-2">
                        <TIcon className="h-4 w-4" style={{ color: t.color }} />
                        <span>{t.label}</span>
                        <span className="text-xs text-muted-foreground">- {t.desc}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Color</Label>
            <div className="flex gap-2">
              {['#6366f1', '#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#ec4899'].map(color => (
                <button
                  key={color}
                  onClick={() => setForm(f => ({ ...f, color }))}
                  className={cn(
                    'w-8 h-8 rounded-full transition-transform',
                    form.color === color && 'ring-2 ring-offset-2 ring-slate-500 scale-110'
                  )}
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} data-testid="save-profile-btn">
            {profile ? 'Save Changes' : 'Create Profile'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// User Assignment Dialog
const UserAssignmentDialog = ({ profile, users, isOpen, onClose, onAssign, onUnassign }) => {
  const [selectedUser, setSelectedUser] = useState('');
  const assignedUserIds = profile?.users?.map(u => u.user_id) || [];

  const availableUsers = users.filter(u => !assignedUserIds.includes(u.id));

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Manage Users - {profile?.name}</DialogTitle>
          <DialogDescription>Assign or remove users from this profile.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Assign new user */}
          <div className="space-y-2">
            <Label>Add User to Profile</Label>
            <div className="flex gap-2">
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Select a user..." />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.map(u => (
                    <SelectItem key={u.id} value={String(u.id)}>
                      {u.full_name} ({u.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button 
                onClick={() => { onAssign(parseInt(selectedUser)); setSelectedUser(''); }}
                disabled={!selectedUser}
              >
                <UserPlus className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          {/* Current assigned users */}
          <div className="space-y-2">
            <Label>Assigned Users ({profile?.users?.length || 0})</Label>
            <div className="border rounded-lg divide-y max-h-[200px] overflow-y-auto">
              {profile?.users?.length === 0 ? (
                <p className="p-4 text-sm text-muted-foreground text-center">No users assigned</p>
              ) : (
                profile?.users?.map(u => (
                  <div key={u.user_id} className="flex items-center justify-between p-3">
                    <div>
                      <p className="font-medium text-sm">{u.full_name}</p>
                      <p className="text-xs text-muted-foreground">{u.email}</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => onUnassign(u.user_id)}>
                      <UserMinus className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main component
export default function AccessProfiles() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [matrix, setMatrix] = useState({ columns: [], rows: [], modules: [] });
  const [profiles, setProfiles] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedModule, setSelectedModule] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedModules, setExpandedModules] = useState({});
  const [showOnlyGrantable, setShowOnlyGrantable] = useState(false);
  const [grantablePermissions, setGrantablePermissions] = useState([]);
  
  // Client and Subsidiary filters
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [selectedSubsidiary, setSelectedSubsidiary] = useState('all');
  
  // Dialogs
  const [profileDialog, setProfileDialog] = useState({ open: false, profile: null });
  const [userDialog, setUserDialog] = useState({ open: false, profile: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, profile: null });

  // Fetch data
  const fetchMatrix = async () => {
    try {
      const res = await fetch(`${API_URL}/api/access-profiles/matrix`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMatrix(data);
        // Expand all modules by default
        const expanded = {};
        data.modules.forEach(m => expanded[m] = true);
        setExpandedModules(expanded);
      }
    } catch (err) {
      console.error('Error fetching matrix:', err);
    }
  };

  const fetchProfiles = async () => {
    try {
      const res = await fetch(`${API_URL}/api/access-profiles/profiles?include_inactive=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProfiles(data.profiles);
      }
    } catch (err) {
      console.error('Error fetching profiles:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // API returns array directly, not wrapped in users object
        setUsers(Array.isArray(data) ? data : data.users || []);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchGrantablePermissions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/delegated-admin/grantable`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Extract permission codes from grantable permissions
        setGrantablePermissions(data.grantable_permissions || []);
      }
    } catch (err) {
      console.error('Error fetching grantable permissions:', err);
      // Fallback: if admin, assume all are grantable
      setGrantablePermissions([]);
    }
  };

  // Fetch clients and their subsidiaries (businesses)
  const fetchClients = async () => {
    try {
      const res = await fetch(`${API_URL}/api/tenants/clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setClients(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
      // If API not available, use empty list
      setClients([]);
    }
  };

  // Fetch client details (with businesses/subsidiaries)
  const fetchClientDetails = async (clientId) => {
    if (clientId === 'all' || !clientId) {
      setSubsidiaries([]);
      setSelectedSubsidiary('all');
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/tenants/clients/${clientId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSubsidiaries(data.businesses || []);
      }
    } catch (err) {
      console.error('Error fetching client details:', err);
      setSubsidiaries([]);
    }
  };

  // Handle client change
  const handleClientChange = (clientId) => {
    setSelectedClient(clientId);
    setSelectedSubsidiary('all');
    if (clientId !== 'all') {
      fetchClientDetails(clientId);
    } else {
      setSubsidiaries([]);
    }
  };

  const seedData = async () => {
    try {
      const res = await fetch(`${API_URL}/api/access-profiles/seed`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.seeded) {
          toast.success(`Seeded ${data.capabilities} capabilities and ${data.profiles} profiles`);
        }
        return data.seeded;
      }
    } catch (err) {
      console.error('Error seeding:', err);
    }
    return false;
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await seedData();
      await Promise.all([fetchMatrix(), fetchProfiles(), fetchUsers(), fetchGrantablePermissions(), fetchClients()]);
      setLoading(false);
    };
    load();
  }, [token]);

  // Handlers
  const handlePermissionChange = async (profileId, capabilityId, newState) => {
    try {
      const res = await fetch(
        `${API_URL}/api/access-profiles/matrix/${profileId}/capability/${capabilityId}?state=${newState}`,
        { method: 'PUT', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        // Update local state
        setMatrix(prev => ({
          ...prev,
          rows: prev.rows.map(row => {
            if (row.id !== profileId) return row;
            const permissions = row.permissions.map(p => 
              p.capability_id === capabilityId ? { ...p, state: newState } : p
            );
            return { ...row, permissions };
          })
        }));
        toast.success('Permission updated');
      }
    } catch (err) {
      toast.error('Failed to update permission');
    }
  };

  const handleSaveProfile = async (form) => {
    try {
      const isEdit = !!profileDialog.profile;
      const url = isEdit 
        ? `${API_URL}/api/access-profiles/profiles/${profileDialog.profile.id}`
        : `${API_URL}/api/access-profiles/profiles`;
      
      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(form)
      });

      if (res.ok) {
        toast.success(isEdit ? 'Profile updated' : 'Profile created');
        setProfileDialog({ open: false, profile: null });
        await Promise.all([fetchMatrix(), fetchProfiles()]);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save profile');
      }
    } catch (err) {
      toast.error('Failed to save profile');
    }
  };

  const handleDeleteProfile = async () => {
    if (!deleteConfirm.profile) return;
    try {
      const res = await fetch(`${API_URL}/api/access-profiles/profiles/${deleteConfirm.profile.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Profile deleted');
        setDeleteConfirm({ open: false, profile: null });
        await Promise.all([fetchMatrix(), fetchProfiles()]);
      }
    } catch (err) {
      toast.error('Failed to delete profile');
    }
  };

  const handleAssignUser = async (userId) => {
    if (!userDialog.profile) return;
    try {
      const res = await fetch(`${API_URL}/api/access-profiles/profiles/${userDialog.profile.id}/users`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ user_id: userId })
      });
      if (res.ok) {
        toast.success('User assigned');
        // Refresh profile details
        const detailRes = await fetch(`${API_URL}/api/access-profiles/profiles/${userDialog.profile.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (detailRes.ok) {
          const data = await detailRes.json();
          setUserDialog(prev => ({ ...prev, profile: data }));
        }
      }
    } catch (err) {
      toast.error('Failed to assign user');
    }
  };

  const handleUnassignUser = async (userId) => {
    if (!userDialog.profile) return;
    try {
      const res = await fetch(
        `${API_URL}/api/access-profiles/profiles/${userDialog.profile.id}/users/${userId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        toast.success('User removed');
        // Refresh profile details
        const detailRes = await fetch(`${API_URL}/api/access-profiles/profiles/${userDialog.profile.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (detailRes.ok) {
          const data = await detailRes.json();
          setUserDialog(prev => ({ ...prev, profile: data }));
        }
      }
    } catch (err) {
      toast.error('Failed to remove user');
    }
  };

  const handleOpenUserDialog = async (profile) => {
    try {
      const res = await fetch(`${API_URL}/api/access-profiles/profiles/${profile.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUserDialog({ open: true, profile: data });
      }
    } catch (err) {
      toast.error('Failed to load profile details');
    }
  };

  const handleApplyTemplate = async (profileId, template) => {
    try {
      const res = await fetch(
        `${API_URL}/api/access-profiles/matrix/${profileId}/apply-template?template=${template}`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        toast.success(`Applied ${template} template`);
        await fetchMatrix();
      }
    } catch (err) {
      toast.error('Failed to apply template');
    }
  };

  // Filter columns by module
  const filteredColumns = useMemo(() => {
    let cols = selectedModule === 'all' ? matrix.columns : matrix.columns.filter(c => c.module === selectedModule);
    
    // If showing only grantable, filter to only grantable permissions
    if (showOnlyGrantable && grantablePermissions.length > 0) {
      cols = cols.filter(c => grantablePermissions.includes(c.code));
    }
    
    return cols;
  }, [matrix.columns, selectedModule, showOnlyGrantable, grantablePermissions]);

  // Check if a capability is grantable by current user
  const isCapabilityGrantable = (capabilityCode) => {
    // If no grantable permissions loaded, assume all are grantable (platform admin)
    if (grantablePermissions.length === 0) return true;
    return grantablePermissions.includes(capabilityCode);
  };

  // Filter rows by search
  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return matrix.rows;
    const q = searchQuery.toLowerCase();
    return matrix.rows.filter(r => 
      r.name.toLowerCase().includes(q) || r.template?.toLowerCase().includes(q)
    );
  }, [matrix.rows, searchQuery]);

  // Group columns by module
  const groupedColumns = useMemo(() => {
    const groups = {};
    filteredColumns.forEach(col => {
      if (!groups[col.module]) groups[col.module] = [];
      groups[col.module].push(col);
    });
    return groups;
  }, [filteredColumns]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="access-profiles-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Access Profiles & Permissions</h1>
          <p className="text-muted-foreground">Manage user roles and granular access control</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => Promise.all([fetchMatrix(), fetchProfiles()])}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={() => setProfileDialog({ open: true, profile: null })} data-testid="create-profile-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Profile
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                <Shield className="h-5 w-5 text-violet-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{profiles.length}</p>
                <p className="text-xs text-muted-foreground">Total Profiles</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Settings className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{matrix.columns.length}</p>
                <p className="text-xs text-muted-foreground">Capabilities</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Box className="h-5 w-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{matrix.modules.length}</p>
                <p className="text-xs text-muted-foreground">Modules</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{users.length}</p>
                <p className="text-xs text-muted-foreground">Total Users</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs defaultValue="matrix" className="space-y-4">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="matrix" data-testid="matrix-tab">
              <LayoutDashboard className="h-4 w-4 mr-2" />
              Permission Matrix
            </TabsTrigger>
            <TabsTrigger value="profiles" data-testid="profiles-tab">
              <Shield className="h-4 w-4 mr-2" />
              Profiles
            </TabsTrigger>
          </TabsList>
          
          <div className="flex items-center gap-2 flex-wrap">
            {/* Client Filter */}
            <Select value={selectedClient} onValueChange={handleClientChange}>
              <SelectTrigger className="w-[160px]" data-testid="client-filter">
                <Building2 className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Clients" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Clients</SelectItem>
                {clients.map(c => (
                  <SelectItem key={c.id} value={c.id.toString()}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Subsidiaries Filter */}
            <Select 
              value={selectedSubsidiary} 
              onValueChange={setSelectedSubsidiary}
              disabled={selectedClient === 'all'}
            >
              <SelectTrigger className="w-[160px]" data-testid="subsidiary-filter">
                <Building className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Subsidiaries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subsidiaries</SelectItem>
                {subsidiaries.map(s => (
                  <SelectItem key={s.id} value={s.id.toString()}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Module Filter */}
            <Select value={selectedModule} onValueChange={setSelectedModule}>
              <SelectTrigger className="w-[180px]" data-testid="module-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modules</SelectItem>
                {matrix.modules.map(m => {
                  const config = MODULE_CONFIG[m] || { name: m, color: '#6366f1' };
                  return (
                    <SelectItem key={m} value={m}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
                        {config.name}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Search profiles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-[200px]"
              />
            </div>
            <div className="flex items-center gap-2 ml-2 pl-2 border-l">
              <Switch
                id="grantable-toggle"
                checked={showOnlyGrantable}
                onCheckedChange={setShowOnlyGrantable}
                data-testid="grantable-permissions-toggle"
              />
              <Label htmlFor="grantable-toggle" className="text-sm cursor-pointer">
                {showOnlyGrantable ? (
                  <span className="flex items-center gap-1">
                    <Eye className="h-3.5 w-3.5" />
                    Grantable Only
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    <EyeOff className="h-3.5 w-3.5" />
                    All Permissions
                  </span>
                )}
              </Label>
            </div>
          </div>
        </div>

        {/* Permission Matrix Tab */}
        <TabsContent value="matrix" className="space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Permission Matrix</CardTitle>
              <CardDescription>
                Click on cells to toggle permissions: 
                <span className="inline-flex items-center gap-1 ml-2">
                  <span className="inline-block w-4 h-4 rounded bg-emerald-500"></span> Allow
                </span>
                <span className="inline-flex items-center gap-1 ml-2">
                  <span className="inline-block w-4 h-4 rounded bg-red-500"></span> Deny
                </span>
                <span className="inline-flex items-center gap-1 ml-2">
                  <span className="inline-block w-4 h-4 rounded bg-slate-300 dark:bg-slate-600"></span> Not Set (Inherit)
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-auto max-h-[60vh]">
                {/* Header row with module groups */}
                <div className="flex sticky top-0 z-20 bg-card border-b">
                  {/* Empty corner cell */}
                  <div className="sticky left-0 z-30 bg-card min-w-[220px] w-[220px] border-r border-b p-3">
                    <p className="text-sm font-medium text-muted-foreground">Profiles</p>
                  </div>
                  
                  {/* Module headers */}
                  <div className="flex">
                    {Object.entries(groupedColumns).map(([module, caps]) => {
                      const config = MODULE_CONFIG[module] || { name: module, color: '#6366f1' };
                      return (
                        <div 
                          key={module}
                          className="border-l-2 px-2 py-1"
                          style={{ 
                            borderColor: config.color,
                            minWidth: caps.length * 44 + 'px'
                          }}
                        >
                          <p className="text-xs font-semibold" style={{ color: config.color }}>
                            {config.name}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Capability column headers */}
                <div className="flex sticky top-[41px] z-20 bg-muted/50 border-b">
                  <div className="sticky left-0 z-30 bg-muted/50 min-w-[220px] w-[220px] border-r" />
                  <div className="flex">
                    {filteredColumns.map(cap => {
                      const moduleColor = MODULE_CONFIG[cap.module]?.color || '#6366f1';
                      return (
                        <CapabilityHeader 
                          key={cap.id} 
                          capability={cap} 
                          moduleColor={moduleColor}
                        />
                      );
                    })}
                  </div>
                </div>
                
                {/* Profile rows */}
                {filteredRows.map(row => (
                  <ProfileRow
                    key={row.id}
                    profile={row}
                    columns={filteredColumns}
                    onPermissionChange={handlePermissionChange}
                    onEdit={(p) => setProfileDialog({ open: true, profile: p })}
                    onDelete={(p) => setDeleteConfirm({ open: true, profile: p })}
                    expandedModules={expandedModules}
                    isCapabilityGrantable={isCapabilityGrantable}
                  />
                ))}
                
                {filteredRows.length === 0 && (
                  <div className="p-8 text-center text-muted-foreground">
                    <Shield className="h-12 w-12 mx-auto mb-4 opacity-20" />
                    <p>No profiles found</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Profiles List Tab */}
        <TabsContent value="profiles" className="space-y-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {profiles.map(profile => {
              const Icon = ICON_MAP[profile.icon] || Shield;
              const templateConfig = DEFAULT_TEMPLATES.find(t => t.value === profile.template) || DEFAULT_TEMPLATES[5];
              
              return (
                <Card key={profile.id} className="overflow-hidden">
                  <div className="h-2" style={{ backgroundColor: profile.color }} />
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: profile.color }}
                        >
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{profile.name}</h3>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <Badge 
                              variant="outline" 
                              className="text-[10px]"
                              style={{ borderColor: templateConfig.color, color: templateConfig.color }}
                            >
                              {profile.template}
                            </Badge>
                            {profile.is_system && (
                              <Badge variant="secondary" className="text-[10px]">System</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      {!profile.is_system && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => setDeleteConfirm({ open: true, profile })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {profile.description || 'No description provided'}
                    </p>
                    
                    <div className="flex items-center justify-between pt-3 border-t">
                      <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                        <Users className="h-4 w-4" />
                        <span>{profile.user_count} users</span>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleOpenUserDialog(profile)}
                        >
                          <Users className="h-3.5 w-3.5 mr-1.5" />
                          Users
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => setProfileDialog({ open: true, profile })}
                        >
                          <Edit className="h-3.5 w-3.5 mr-1.5" />
                          Edit
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <ProfileDialog
        profile={profileDialog.profile}
        isOpen={profileDialog.open}
        onClose={() => setProfileDialog({ open: false, profile: null })}
        onSave={handleSaveProfile}
      />

      <UserAssignmentDialog
        profile={userDialog.profile}
        users={users}
        isOpen={userDialog.open}
        onClose={() => setUserDialog({ open: false, profile: null })}
        onAssign={handleAssignUser}
        onUnassign={handleUnassignUser}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onOpenChange={() => setDeleteConfirm({ open: false, profile: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Profile</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deleteConfirm.profile?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm({ open: false, profile: null })}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteProfile}>
              Delete Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
